import { Routes, Route, Navigate, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "./auth/useAuth";
import { useUiSettings } from "./api/uiSettings";
import { SettingsView } from "./views/SettingsView";
import { LoginScreen } from "./auth/LoginScreen";
import { PrimeThreadReader } from "./views/PrimeThreadReader";
import { BriefingLogReader } from "./views/BriefingLogReader";
import { SystemLogViewer } from "./views/SystemLogViewer";
import { SchedulerView } from "./views/SchedulerView";
import { TodosView } from "./views/TodosView";
import { AgentActivityView } from "./views/AgentActivityView";
import { SearchView } from "./views/SearchView";
import { ChipsView } from "./views/ChipsView";
import { TranscriptsView } from "./views/TranscriptsView";
import { UploadView } from "./views/UploadView";

export function App() {
  const auth = useAuth();
  const location = useLocation();
  const { data: uiSettings } = useUiSettings();

  if (auth.status === "loading") {
    return (
      <div className="app-splash">
        <div className="spinner" />
      </div>
    );
  }

  if (auth.status === "unauthenticated") {
    return <LoginScreen />;
  }

  const calmOn = uiSettings?.calm_transcripts === true;

  // Family accounts get a single-purpose app: the transcripts, nothing else.
  // (The server enforces this regardless — this is just the matching UI.)
  if (auth.role === "family") {
    return (
      <div className={`app-shell${calmOn ? " calm" : ""}`}>
        <header className="app-header">
          <h1>Appointment Transcripts</h1>
          <div className="app-header-right">
            <span className="app-user">{auth.email}</span>
            <button className="btn-link" onClick={auth.logout}>
              Log out
            </button>
          </div>
        </header>
        <nav className="app-nav">
          <NavLink to="/" end>Transcripts</NavLink>
          <NavLink to="/upload">Upload</NavLink>
        </nav>
        <main className="app-main">
          <Routes>
            <Route path="/upload" element={<UploadView />} />
            <Route path="*" element={<TranscriptsView />} />
          </Routes>
        </main>
      </div>
    );
  }

  // Admin sees the calm override only on the transcription-related tabs.
  const onTranscriptionTab =
    location.pathname.startsWith("/transcripts") || location.pathname.startsWith("/upload");

  return (
    <div className={`app-shell${calmOn && onTranscriptionTab ? " calm" : ""}`}>
      <header className="app-header">
        <h1>Jarvis Dev</h1>
        <div className="app-header-right">
          <span className="app-user">{auth.email}</span>
          <button className="btn-link" onClick={auth.logout}>
            Log out
          </button>
        </div>
      </header>
      <nav className="app-nav">
        <NavLink to="/" end>Prime Thread</NavLink>
        <NavLink to="/agents">Agents</NavLink>
        <NavLink to="/briefings">Briefings</NavLink>
        <NavLink to="/errors">Errors</NavLink>
        <NavLink to="/scheduler">Scheduler</NavLink>
        <NavLink to="/todos">Todos</NavLink>
        <NavLink to="/search">Search</NavLink>
        <NavLink to="/chips">Chips</NavLink>
        <NavLink to="/transcripts">Transcripts</NavLink>
        <NavLink to="/upload">Upload</NavLink>
        <NavLink to="/settings">Settings</NavLink>
      </nav>
      <main className="app-main">
        <Routes>
          <Route path="/" element={<PrimeThreadReader />} />
          <Route path="/agents" element={<AgentActivityView />} />
          <Route path="/briefings" element={<BriefingLogReader />} />
          <Route path="/errors" element={<SystemLogViewer />} />
          <Route path="/scheduler" element={<SchedulerView />} />
          <Route path="/todos" element={<TodosView />} />
          <Route path="/search" element={<SearchView />} />
          <Route path="/chips" element={<ChipsView />} />
          <Route path="/transcripts" element={<TranscriptsView />} />
          <Route path="/upload" element={<UploadView />} />
          <Route path="/settings" element={<SettingsView />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
